import React, { useEffect, useMemo } from "react";
import { useAppDispatch } from "../../../../../../redux/store";

// Import types e actions
import {Assegnazione, OrdineLavoro} from "../../ordiniLavoroTypes";
import { saveAssegnazione } from "../../ordiniLavoroSlice";
import { Macchina } from "components/anagrafiche/anagraficheSlice";

// Form
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import useAutoSave from "components/common/formUtility/useAutoSave";
import AutoSaveIndicator from "components/common/formUtility/AutoSaveIndicator";

// Date utils
import { convertToDateTime } from "components/common/dateUtils/convertToDateTime";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

// Material UI
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import {ErrorStatusTypes} from "../../../../../common/errorsDeclarations";

// ============================================
// INTERFACES
// ============================================

interface AssegnazioneInlineFormProps {
    assegnazione: Assegnazione;
    ordineLavoro: OrdineLavoro;
    macchine: Macchina[];
    isNew: boolean;
    onCancel?: () => void;
    onDelete?: () => void;
    onSaveSuccess?: () => void;
}

// ============================================
// COMPONENT
// ============================================

const AssegnazioneInlineForm = ({
                                    assegnazione,
                                    ordineLavoro,
                                    macchine,
                                    isNew,
                                    onCancel,
                                    onDelete,
                                    onSaveSuccess,
                                }: AssegnazioneInlineFormProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
// ============================================
    // FILTRO MACCHINE PER TECNICA
    // ============================================

    const macchineFiltered = useMemo(() => {
        if (!ordineLavoro?.tecnica) {
            return macchine;
        }
        return macchine.filter((macchina) =>
            macchina.tecniche?.includes(ordineLavoro.tecnica || 999)
        );
    }, [macchine, ordineLavoro?.tecnica]);

    // ============================================
    // REACT HOOK FORM SETUP
    // ============================================

    const {
        handleSubmit,
        register,
        formState: { errors, isDirty, isValid },
        setValue,
        watch,
        control,
        setError,
        clearErrors,
        reset,
    } = useForm<Assegnazione>({
        mode: "onChange",
        defaultValues: assegnazione,
    });

    // ============================================
    // GESTIONE ERRORI SERVER
    // ============================================

    const listaCampi = useMemo(
        () => Object.keys(assegnazione) as Array<keyof Assegnazione>,
        [assegnazione]
    );

    // Reset form quando cambiano i dati
    useEffect(() => {
        reset(assegnazione, { keepDirty: false });
    }, [assegnazione.id, reset]);

    // Gestione errori dal server sui campi
    useEffect(() => {
        clearErrors();
        listaCampi.forEach((field) => {
            if (assegnazione?.errorsStack?.fieldsErrors?.[field]) {
                setError(field, {
                    type: "server",
                    message: assegnazione.errorsStack.fieldsErrors[field].toString(),
                });
            }
        });
    }, [assegnazione?.errorsStack?.fieldsErrors, listaCampi, setError, clearErrors]);

    // ============================================
    // SUBMIT HANDLER
    // ============================================

    const onSubmit: SubmitHandler<Assegnazione> = async (data) => {
        const result = await dispatch(
            saveAssegnazione({
                ordineLavoroId: ordineLavoro.id!,
                assegnazione: data,
            })
        );

        if (result.meta.requestStatus === "fulfilled") {
            if (isNew && onSaveSuccess) {
                onSaveSuccess();
            } else {
                reset(data);
            }
        }
    };

    // ============================================
    // AUTOSAVE - Solo per assegnazioni esistenti
    // ============================================

    useAutoSave({
        watch,
        handleSubmit,
        isDirty,
        isValid,
        delay: 600,
        onSubmit,
        canSubmit: !!assegnazione.id && !!ordineLavoro?.id, // Solo se esiste già
    });

    // ============================================
    // RENDER
    // ============================================

    return (
        <form onSubmit={handleSubmit(onSubmit)} key={`assegnazione-form-${macchineFiltered.length || "new"}`}>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
                {/* Macchina */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Controller
                        name="macchina"
                        control={control}
                        rules={{ required: "Macchina obbligatoria" }}
                        render={({ field }) => (
                            <Autocomplete
                                value={macchineFiltered.find((m) => m.id === field.value) || null}
                                options={macchineFiltered}
                                getOptionLabel={(option) =>
                                    option.codice
                                        ? `${option.codice}${option.descrizione ? ` - ${option.descrizione}` : ""}`
                                        : ""
                                }
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, newValue) => {
                                    clearErrors("macchina");
                                    setValue("macchina", newValue?.id || undefined, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    setValue("macchina_display", newValue?.codice || "", {
                                        shouldDirty: true,
                                    });

                                    // AutoSave immediato se esiste
                                    if (assegnazione.id) {
                                        handleSubmit(onSubmit)();
                                    }
                                }}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Macchina"
                                        required
                                        error={!!errors?.macchina}
                                        helperText={errors?.macchina?.message}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>

                {/* Data Da */}
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <DesktopDatePicker
                        label="Data Da"
                        value={convertToDateTime(watch("data_da"))}
                        onChange={(newValue) => {
                            setValue(
                                "data_da",
                                newValue
                                    ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || ""
                                    : null,
                                { shouldDirty: true }
                            );
                        }}
                        slotProps={{
                            textField: {
                                size: "small",
                                error: !!errors?.data_da,
                                helperText: errors?.data_da?.message,
                                fullWidth: true,
                            },
                            field: {
                                clearable: true,
                            },
                        }}
                    />
                </Grid>

                {/* Data A */}
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <DesktopDatePicker
                        label="Data A"
                        value={convertToDateTime(watch("data_a"))}
                        onChange={(newValue) => {
                            setValue(
                                "data_a",
                                newValue
                                    ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || ""
                                    : null,
                                { shouldDirty: true }
                            );
                        }}
                        slotProps={{
                            textField: {
                                size: "small",
                                error: !!errors?.data_a,
                                helperText: errors?.data_a?.message,
                                fullWidth: true,
                            },
                            field: {
                                clearable: true,
                            },
                        }}
                    />
                </Grid>

                {/* Note */}
                <Grid size={{ xs: 12, sm: isNew ? 6 : 4, md: isNew ? 3 : 3 }}>
                    <TextField
                        label="Note"
                        size="small"
                        variant="outlined"
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        {...register("note")}
                        error={!!errors?.note}
                        helperText={errors?.note?.message}
                        fullWidth
                    />
                </Grid>

                {/* Azioni */}
                <Grid size={{ xs: 12, sm: isNew ? 6 : 2, md: 2 }}>
                    {isNew ? (
                        // Bottoni per nuova assegnazione
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={onCancel}
                            >
                                Annulla
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<SaveIcon />}
                                type="submit"
                                disabled={!isDirty || !isValid}
                            >
                                Salva
                            </Button>
                        </Stack>
                    ) : (
                        // Azioni per assegnazione esistente
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ justifyContent: "flex-end", alignItems: "center" }}
                        >
                            <Typography variant="body2" >

                                {assegnazione.valore_schedulato && Intl.NumberFormat("it-IT", {
                                    style: "decimal",
                                    useGrouping: true,
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                }).format(assegnazione.valore_schedulato)} €
                            </Typography>
                            <AutoSaveIndicator
                                isDirty={isDirty}
                                status={assegnazione.errorsStack?.status|| ErrorStatusTypes.OK}
                                variant="chip"
                                size="small"
                            />
                            <IconButton
                                size="small"
                                color="error"
                                onClick={onDelete}
                                title="Elimina assegnazione"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </form>
    );
};

export default AssegnazioneInlineForm;